<template>
  <dp-seite titel="Protokoll" untertitel="Entwurfsänderungen und Veröffentlichungen" :snack="snack">
    <template #aktionen>
      <v-btn small depressed @click="neuLaden"><v-icon small left>mdi-refresh</v-icon>Aktualisieren</v-btn>
    </template>

    <v-tabs v-model="tab" class="mb-3" height="38">
      <v-tab>Entwurfsänderungen</v-tab>
      <v-tab>Vorschauen &amp; Veröffentlichungen</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- draft change log -->
      <v-tab-item>
        <v-data-table dense :headers="aenderungHeaders" :items="aenderungen" item-key="seq" class="elevation-1" hide-default-footer disable-pagination
                      :loading="laedt.aenderungen" no-data-text="Noch keine Entwurfsänderungen.">
          <template v-slot:item.erstellt_am="{ item }"><span class="text-no-wrap">{{ zeit(item.erstellt_am) }}</span></template>
          <template v-slot:item.typ="{ item }">
            <v-chip x-small outlined :color="item.typ === 'RUECKNAHME' ? 'orange darken-2' : 'primary'">{{ typText(item.typ) }}</v-chip>
          </template>
          <template v-slot:item.zusammenfassung="{ item }">{{ item.zusammenfassung || '–' }}</template>
          <template v-slot:item.aktion="{ item }">
            <v-btn x-small text color="primary" :disabled="!item.details" @click="detailsOeffnen(item)">Details</v-btn>
          </template>
        </v-data-table>
        <div v-if="mehr" class="text-center mt-3">
          <v-btn small depressed :loading="laedt.aenderungen" @click="aeltereLaden"><v-icon small left>mdi-chevron-down</v-icon>Ältere laden</v-btn>
        </div>
      </v-tab-item>

      <!-- preview / publication runs -->
      <v-tab-item>
        <v-data-table dense :headers="laufHeaders" :items="laeufe" item-key="id" class="elevation-1 dp-klickbar" :items-per-page="25"
                      :loading="laedt.laeufe" no-data-text="Noch keine Vorschau oder Veröffentlichung." @click:row="laufOeffnen">
          <template v-slot:item.erstellt_am="{ item }"><span class="text-no-wrap">{{ zeit(item.erstellt_am) }}</span></template>
          <template v-slot:item.modus="{ item }">{{ modusText(item.modus) }}</template>
          <template v-slot:item.zeitraum="{ item }"><span class="text-no-wrap">{{ fmtDay(item.von) }} – {{ fmtDay(item.bis) }}</span></template>
          <template v-slot:item.status="{ item }">
            <v-chip x-small dark :color="status(item.status).color">{{ status(item.status).text }}</v-chip>
            <div v-if="item.fehlercode" class="text-caption red--text">{{ fehlerText(item.fehlercode) }}</div>
          </template>
          <template v-slot:item.von_benutzer="{ item }">
            {{ item.angefordert_von }}<span v-if="item.freigegeben_von" class="grey--text"> / {{ item.freigegeben_von }}</span>
          </template>
          <template v-slot:item.nachberechnung="{ item }">{{ nachberechnung(item.nachberechnung) }}</template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <div class="dp-hint mt-4">
      <v-icon small color="primary">mdi-information-outline</v-icon>
      <span>Bearbeitungen am Entwurf überschreiben keine früheren Läufe; jeder Lauf hält die geprüften und geschriebenen Werte fest.</span>
    </div>

    <bestaetigen-dialog ref="details" :titel="detailsTitel" breit>
      <v-simple-table dense class="dp-table">
        <thead><tr><th>Art</th><th>Schlüssel</th><th>Vorher</th><th>Nachher</th></tr></thead>
        <tbody>
        <tr v-for="(d, i) in details" :key="i">
          <td class="text-no-wrap">{{ d.art }}</td>
          <td>{{ schluessel(d) }}</td>
          <td class="grey--text text--darken-1">
            <template v-if="wert(d.art, d.vorher) !== null">{{ wert(d.art, d.vorher) }}</template>
            <pre v-else class="dp-json">{{ json(d.vorher) }}</pre>
          </td>
          <td>
            <template v-if="wert(d.art, d.nachher) !== null">{{ wert(d.art, d.nachher) }}</template>
            <pre v-else class="dp-json">{{ json(d.nachher) }}</pre>
          </td>
        </tr>
        <tr v-if="!details.length"><td colspan="4" class="grey--text">Keine Details gespeichert.</td></tr>
        </tbody>
      </v-simple-table>
    </bestaetigen-dialog>

    <bestaetigen-dialog ref="lauf" :titel="laufTitel" breit>
      <div v-if="lauf" class="d-flex flex-wrap align-center mb-2" style="gap: 6px">
        <v-chip x-small dark :color="status(lauf.status).color">{{ status(lauf.status).text }}</v-chip>
        <v-chip x-small outlined>Entwurf Rev. {{ lauf.entwurf_revision }}</v-chip>
        <v-chip x-small outlined>Feiertagsregion {{ lauf.feiertagsregion || '–' }}</v-chip>
        <v-chip x-small outlined color="success">{{ lauf.anzahl_neu }} neu</v-chip>
        <v-chip x-small outlined color="primary">{{ lauf.anzahl_geaendert }} geändert</v-chip>
        <v-chip x-small outlined>{{ lauf.anzahl_unveraendert }} unverändert</v-chip>
        <v-chip x-small outlined color="warning">{{ lauf.anzahl_gesperrt }} gesperrt</v-chip>
        <v-chip x-small outlined color="error">{{ lauf.anzahl_fehler }} Fehler</v-chip>
      </div>
      <v-data-table dense :headers="zeileHeaders" :items="zeilen" :items-per-page="50" :footer-props="{'items-per-page-options': [50, 100, -1]}"
                    no-data-text="Keine Zeilen.">
        <template v-slot:item.datum="{ item }">
          <span class="text-no-wrap">{{ fmtDay(item.datum) }}</span>
          <v-chip v-if="item.ist_feiertag" x-small outlined color="deep-purple" class="ml-1">{{ item.feiertagsbezeichnung || 'Feiertag' }}</v-chip>
        </template>
        <template v-slot:item.mita_bis_id="{ item }">{{ name(item.mita_bis_id) }}</template>
        <template v-slot:item.quelle_typ="{ item }">{{ quelleText(item.quelle_typ) }}</template>
        <template v-slot:item.vorher_json="{ item }"><span class="text-no-wrap grey--text text--darken-1">{{ werte(item.vorher_json) }}</span></template>
        <template v-slot:item.nachher_json="{ item }"><span class="text-no-wrap font-weight-medium">{{ werte(item.nachher_json) }}</span></template>
        <template v-slot:item.aktion="{ item }">
          <v-chip x-small dark :color="aktion(item.aktion).color">{{ aktion(item.aktion).text }}</v-chip>
          <div v-if="item.fehlercode" class="text-caption red--text">{{ fehlerText(item.fehlercode) }}</div>
        </template>
      </v-data-table>
    </bestaetigen-dialog>
  </dp-seite>
</template>

<script>
import {DateTime} from 'luxon'
import {ACTIONS, dpApi, ERROR_CODES, fmtDay, fmtHours, QUELLEN, SOURCES, ZIELE} from '@/shared/dienstplan_api'
import seite from './seite'
import {dp, gruppeName, name, position} from './store'
import DpSeite from './components/DpSeite'
import BestaetigenDialog from './components/BestaetigenDialog'

const SEITE = 50
const TYPEN = {ENTWURF: 'Entwurf', RUECKNAHME: 'Rücknahme'}
const MODI = {PREVIEW: 'Vorschau', PUBLISH: 'Veröffentlichung', ROLLBACK: 'Rücknahme'}
const STATUS = {
  OK: {text: 'OK', color: 'success'},
  BLOCKED: {text: 'gesperrt', color: 'orange darken-1'},
  ERROR: {text: 'Fehler', color: 'error'},
}
const NACHBERECHNUNG = {NONE: '–', PENDING: 'läuft', DONE: 'abgeschlossen', ERROR: 'fehlgeschlagen'}

export default {
  name: 'DpProtokoll',
  mixins: [seite],
  components: {DpSeite, BestaetigenDialog},
  data: () => ({
    tab: 0,
    aenderungen: [],
    mehr: false,
    laeufe: [],
    laeufeGeladen: false,
    laedt: {aenderungen: false, laeufe: false},
    detailsTitel: '',
    details: [],
    lauf: null,
    zeilen: [],
    aenderungHeaders: [
      {text: 'Nr.', value: 'seq', width: 70},
      {text: 'Rev.', value: 'revision', width: 70},
      {text: 'Zeit', value: 'erstellt_am'},
      {text: 'Benutzer', value: 'erstellt_von'},
      {text: 'Typ', value: 'typ'},
      {text: 'Zusammenfassung', value: 'zusammenfassung', sortable: false},
      {text: '', value: 'aktion', sortable: false, align: 'end'},
    ],
    laufHeaders: [
      {text: 'Lauf', value: 'seq', width: 70},
      {text: 'Zeit', value: 'erstellt_am'},
      {text: 'Art', value: 'modus'},
      {text: 'Zeitraum', value: 'zeitraum', sortable: false},
      {text: 'Status', value: 'status'},
      {text: 'Neu', value: 'anzahl_neu', align: 'end'},
      {text: 'Geändert', value: 'anzahl_geaendert', align: 'end'},
      {text: 'Unverändert', value: 'anzahl_unveraendert', align: 'end'},
      {text: 'Gesperrt', value: 'anzahl_gesperrt', align: 'end'},
      {text: 'Fehler', value: 'anzahl_fehler', align: 'end'},
      {text: 'Von', value: 'von_benutzer', sortable: false},
      {text: 'Neuberechnung', value: 'nachberechnung'},
    ],
    zeileHeaders: [
      {text: 'Datum', value: 'datum'},
      {text: 'Mitarbeiter', value: 'mita_bis_id'},
      {text: 'Quelle', value: 'quelle_typ'},
      {text: 'Bisher', value: 'vorher_json', sortable: false},
      {text: 'Neu', value: 'nachher_json', sortable: false},
      {text: 'Aktion', value: 'aktion'},
    ],
  }),
  computed: {
    laufTitel () {
      const l = this.lauf
      return l ? 'Lauf ' + l.seq + ' · ' + this.modusText(l.modus) + ' · ' + fmtDay(l.von) + ' – ' + fmtDay(l.bis) : 'Lauf'
    },
  },
  watch: {
    tab (t) {
      if (t === 1 && !this.laeufeGeladen) this.laeufeLaden()
    },
  },
  created () {
    this.ensure(false)
    this.aenderungenLaden()
  },
  methods: {
    fmtDay,
    name,
    typText: t => TYPEN[t] || t,
    modusText: m => MODI[m] || m,
    status: s => STATUS[s] || {text: s, color: 'grey'},
    aktion: a => ACTIONS[a] || {text: a, color: 'grey'},
    nachberechnung: n => NACHBERECHNUNG[n] || '–',
    quelleText: q => SOURCES[q] || q,
    fehlerText: c => ERROR_CODES[c] || c,
    zeit (s) {
      if (!s) return ''
      let d = DateTime.fromSQL(s)
      if (!d.isValid) d = DateTime.fromISO(s)
      return d.isValid ? d.toFormat('dd.MM.yyyy HH:mm') : s
    },
    neuLaden () {
      this.aenderungenLaden()
      if (this.laeufeGeladen || this.tab === 1) this.laeufeLaden()
    },
    // ----- draft change log -----
    aenderungenLaden (vor) {
      this.laedt.aenderungen = true
      return dpApi.protokoll(vor).then(list => {
        list = list || []
        this.aenderungen = vor ? this.aenderungen.concat(list) : list
        this.mehr = list.length >= SEITE
        this.laedt.aenderungen = false
      }, res => {
        this.laedt.aenderungen = false
        this.fail(res)
      })
    },
    aeltereLaden () {
      const last = this.aenderungen[this.aenderungen.length - 1]
      if (last) this.aenderungenLaden(last.seq)
    },
    detailsOeffnen (item) {
      let list = []
      try {
        list = JSON.parse(item.details) || []
      } catch (e) {
        list = []
      }
      this.details = Array.isArray(list) ? list : [list]
      this.detailsTitel = 'Änderung Nr. ' + item.seq + ' · Rev. ' + item.revision + ' · ' + this.zeit(item.erstellt_am)
      this.$refs.details.open()
    },
    rasterName (z) {
      return z.raster_name || (dp.basis.raster.find(r => r.bis_id === z.azra) || {}).name || z.azra
    },
    schluessel (d) {
      const p = String(d.schluessel || '').split('|')
      switch (d.art) {
        case 'Musterwoche':
          if (p.length === 3) return (ZIELE[p[0]] || p[0]) + ' ' + (p[0] === 'DIRECT' ? name(p[1]) : gruppeName(p[1])) + ' · ' + p[2]
          break
        case 'Wochenquelle':
          if (p.length === 2) return name(p[0]) + ' · ' + p[1]
          break
        case 'Tagesvertretung':
          if (p.length === 2) return name(p[0]) + ' · ' + fmtDay(p[1])
          break
        case 'Rotation':
          return gruppeName(p[0])
      }
      return d.schluessel
    },
    // wert renders a before/after object compactly; null means "show as JSON"
    wert (art, v) {
      if (v === null || v === undefined) return '–'
      if (typeof v !== 'object') return String(v)
      switch (art) {
        case 'Musterwoche':
          return this.rasterName(v) + ' W' + v.woche_nr + ' (' + v.iso_woche + ')'
        case 'Wochenquelle': {
          let text = QUELLEN[v.typ] || v.typ
          if (v.typ === 'CREW') text += ' · ' + gruppeName(v.planungsgruppe_id)
          if (v.typ === 'MACHINE' && v.fahrerposition_id) text += ' · ' + ((position(v.fahrerposition_id) || {}).bezeichnung || 'Fahrerposition')
          return text
        }
        case 'Tagesvertretung':
          return 'vertritt ' + name(v.vertretener_bis_id) + (v.bemerkung ? ' (' + v.bemerkung + ')' : '')
        case 'Rotation':
          return this.rasterName(v) + ' ab ' + fmtDay(v.anker_montag)
      }
      return null
    },
    json: v => JSON.stringify(v, null, 1),
    // ----- runs -----
    laeufeLaden () {
      this.laedt.laeufe = true
      return dpApi.laeufe('', '').then(list => {
        this.laeufe = list || []
        this.laeufeGeladen = true
        this.laedt.laeufe = false
      }, res => {
        this.laedt.laeufe = false
        this.fail(res)
      })
    },
    laufOeffnen (item) {
      this.showLoader()
      dpApi.lauf(item.id).then(res => {
        this.hideLoader()
        this.lauf = res.lauf || item
        this.zeilen = res.zeilen || []
        this.$refs.lauf.open()
      }, res => this.fail(res))
    },
    // "7,75 h · 05:00-13:15 · 09:00-09:30 · Schicht"
    werte (json) {
      if (!json) return '–'
      try {
        const m = JSON.parse(json)
        const teile = [fmtHours(m.zeit_soll) + ' h', m.soll_zeit]
        const pausen = [m.pause_1, m.pause_2].filter(p => p && p !== '00:00-00:00')
        if (pausen.length) teile.push(pausen.join(', '))
        if (m.schicht) teile.push(m.schicht)
        return teile.filter(Boolean).join(' · ')
      } catch (e) {
        return json
      }
    },
  },
}
</script>

<style scoped>
.dp-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #455a64;
}
.dp-table th {
  white-space: nowrap;
}
.dp-json {
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  max-width: 360px;
}
.dp-klickbar >>> tbody tr {
  cursor: pointer;
}
</style>
